import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import AccessGate from "@/components/AccessGate";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Front-end AccessGate is opt-in via env — the marketing/website pages must
// stay publicly viewable. Backend IP allowlist (ACCESS_MODE=enforce in
// backend/.env) still fully protects every /api/* endpoint, so login,
// merchant portal and admin data remain private even though the SPA mounts.
// To re-enable the private-beta splash, set REACT_APP_ACCESS_GATE=on in
// frontend/.env and restart the frontend.
const GATE_ENABLED = process.env.REACT_APP_ACCESS_GATE === "on";
const gated = GATE_ENABLED ? <AccessGate><App /></AccessGate> : <App />;

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {gated}
    </QueryClientProvider>
  </React.StrictMode>,
);

// Iteration 31 — register the PWA service worker only on the customer app host.
// The main merchant site + admin subdomain deliberately don't get service-worker
// caching so operators always see the freshest UI on those experiences.
if ("serviceWorker" in navigator) {
  const isAppHost = (() => {
    try {
      const h = window.location.hostname.toLowerCase();
      const q = new URLSearchParams(window.location.search).get("host");
      const stored = window.localStorage.getItem("cyn_host_override");
      return h.startsWith("app.") || q === "app" || stored === "app";
    } catch (_) { return false; }
  })();
  if (isAppHost) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => null);
    });
  } else {
    // Ensure the SW is unregistered when a visitor lands on the merchant/admin site.
    navigator.serviceWorker.getRegistrations?.().then((regs) => {
      regs.forEach((r) => r.unregister().catch(() => null));
    }).catch(() => null);
  }
}
